import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["sportOption", "layoutOption", "quoteOption", "noteOption", "schoolInput",
                    "notesCount", "layoutsList", "quotesList", "gameNotesList", "quoteTitle",
                    "quoteText", "recapTitle"]
  static values = {
    selectedSport: String
  }

  connect() {
    this.selections = {
      sport: null,
      layout: null,
      quote: null,
      notes: []
    }
  }

  selectSport(event) {
    const option = event.currentTarget
    const sportId = option.dataset.sportId

    // Remove active class from all sport options
    this.sportOptionTargets.forEach(opt => {
      opt.classList.remove('active')
      // opt.querySelector('.sport-img').style.filter = 'grayscale(100%)'
    })

    // Add active class to selected option
    option.classList.add('active')
    // option.querySelector('.sport-img').style.filter = 'none'

    this.selections.sport = sportId
    this.selectedSportValue = sportId
    this.updateSportContent(sportId)
    this.trackSelection('sport_selected', { sport_id: sportId })
  }

  selectLayout(event) {
    const option = event.currentTarget
    const layoutId = option.dataset.layoutId

    // Remove active class from all layout options
    this.layoutOptionTargets.forEach(opt => {
      opt.classList.remove('active')
      // opt.querySelector('.layout-img').style.filter = 'grayscale(100%)'
    })

    // Add active class to selected option
    option.classList.add('active')
    // option.querySelector('.layout-img').style.filter = 'none'

    this.selections.layout = layoutId
    this.trackSelection('layout_selected', { layout_id: layoutId })
  }

  selectQuote(event) {
    const option = event.currentTarget
    const quoteId = option.dataset.quoteId

    // Remove active class from all quote options
    this.quoteOptionTargets.forEach(opt => {
      opt.classList.remove('active')
      // opt.querySelector('.quote-img').style.filter = 'grayscale(100%)'
    })

    // Add active class to selected option
    option.classList.add('active')
    // option.querySelector('.quote-img').style.filter = 'none'

    this.selections.quote = quoteId
    this.trackSelection('quote_selected', { quote_id: quoteId })
  }

  toggleNote(event) {
    const option = event.currentTarget
    const noteId = option.dataset.noteId
    const isActive = option.dataset.noteActive === 'true'

    // Toggle active state
    const newActiveState = !isActive
    option.dataset.noteActive = newActiveState.toString()

    // Update visual state
    const icon = option.querySelector('.note-icon')
    if (newActiveState) {
      option.classList.add('active')
      icon.classList.add('active')
      icon.classList.remove('far', 'fa-circle', 'note-icon')
      icon.classList.add('fas', 'fa-check-circle', 'note-icon')
    } else {
      option.classList.remove('active')
      icon.classList.remove('active')
      icon.classList.remove('fas', 'fa-check-circle', 'note-icon')
      icon.classList.add('far', 'fa-circle', 'note-icon')
    }

    // Update selections array
    if (newActiveState) {
      if (!this.selections.notes.includes(noteId)) {
        this.selections.notes.push(noteId)
      }
    } else {
      this.selections.notes = this.selections.notes.filter(id => id !== noteId)
    }

    this.trackSelection('note_toggled', {
      note_id: noteId,
      active: newActiveState,
      total_notes: this.selections.notes.length
    })
  }

  requestAccess(event) {
    const accessToken = this.getAccessToken()
    if (!accessToken) return

    const requesterInfo = {
      name: prompt('Please enter your name:'),
      email: prompt('Please enter your email:'),
      message: prompt('Please enter any additional message (optional):')
    }

    if (!requesterInfo.name || !requesterInfo.email) {
      alert('Name and email are required.')
      return
    }

    fetch('/showcases/request_access', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({
        access_token: accessToken,
        requester_info: requesterInfo
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.message) {
        alert('Access request sent successfully! You will receive a new link via email.')
      } else {
        alert('Error: ' + (data.error || 'Failed to send request'))
      }
    })
    .catch(error => {
      console.error('Error:', error)
      alert('Failed to send access request. Please try again.')
    })
  }

  getAccessToken() {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('access_token')
  }

  trackSelection(eventType, metadata = {}) {
    const accessToken = this.getAccessToken()
    if (!accessToken) return

    const payload = {
      event_type: eventType,
      page_name: 'showcase_selection',
      metadata: {
        selections: this.selections,
        ...metadata
      }
    }

    fetch(`/showcases/recaps/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({
        access_token: accessToken,
        ...payload
      })
    }).catch(error => {
      console.log('Tracking selection failed:', error)
    })
  }

  updateSportContent(sportSlug) {
    if (!window.showcaseConfig || !window.showcaseConfig.sports) {
      console.error('Showcase config not available')
      return
    }

    const sportData = window.showcaseConfig.sports[sportSlug]
    if (!sportData) {
      console.error(`Sport data not found for: ${sportSlug}`)
      return
    }

    this.renderLayouts(sportData)
    this.renderQuotes(sportData)
    this.renderGameNotes(sportData)
    this.renderRecapTitle(sportData)
  }

  renderLayouts(sportData) {
    if (!this.hasLayoutsListTarget) return

    const layouts = sportData.layouts || []
    const layoutsHtml = layouts.map((layout, index) => {
      const sectionsHtml = Object.entries(layout.sections || {}).map(([sectionTitle, sectionOptions]) => {
        const optionsHtml = sectionOptions.map(option => {
          const activeClass = option.active ? 'bg-fanword-yellow color-fanword-black' : 'bg-fanword-background color-fanword-text fanword-border'
          return `<div class='py-1 px-4 fanword-border-radius ${activeClass} me-3'>${option.title}</div>`
        }).join('')

        return `
          <p class='font-fanword-bold'>${sectionTitle}</p>
          <div class='d-flex flex-row mb-5'>${optionsHtml}</div>
        `
      }).join('')

      const checkboxesHtml = (layout.checkboxes || []).map(checkbox => {
        const checked = checkbox.checked ? 'checked' : ''
        return `<div class='me-3'><input type="checkbox" class='me-2' disabled readonly ${checked}>${checkbox.title}</div>`
      }).join('')

      return `
        <div class="col-md-6">
          <div class="layout-option" data-layout-id="${index}" data-action="click->showcase-selection#selectLayout" data-showcase-selection-target="layoutOption">
            <div class="layout-info">
              <div class="layout-features">
                ${sectionsHtml}
                <hr class='fanword-divider my-3 w-75' />
                <div class='d-flex flex-row mt-2'>${checkboxesHtml}</div>
              </div>
            </div>
          </div>
        </div>
      `
    }).join('')

    this.layoutsListTarget.innerHTML = layoutsHtml
  }

  renderQuotes(sportData) {
    const quotes = sportData.quotes || []

    // Find all quote containers
    const quoteContainers = document.querySelectorAll('.quote-container')

    quoteContainers.forEach((container, index) => {
      if (index < quotes.length) {
        // Show container and update content
        container.style.display = 'block'
        const quote = quotes[index]

        const titleElement = container.querySelector('[data-showcase-selection-target="quoteTitle"]')
        const textElement = container.querySelector('[data-showcase-selection-target="quoteText"]')

        if (titleElement) {
          titleElement.textContent = quote.title
        }
        if (textElement) {
          textElement.innerHTML = `"${quote.text}"`
        }
      } else {
        // Hide container if no quote data for this index
        container.style.display = 'none'
      }
    })
  }

  renderGameNotes(sportData) {
    if (!this.hasGameNotesListTarget) return

    const gameNotes = sportData.game_notes || []
    const notesHtml = gameNotes.map((note, index) => {
      return `
        <div class="col-md-6">
          <div class="game-note-option" data-note-id="${index}" data-note-active="false" data-action="click->showcase-selection#toggleNote" data-showcase-selection-target="noteOption">
            <div class="note-content">
              <div class="note-header">
                <div class='d-flex flex-column'>
                  <img src="${note.image}" alt="${note.title}" class="note-image mb-2" style='width: 50px;'>
                  <h5 class="note-name">${note.title}</h5>
                </div>
                <div class="note-toggle">
                  <i class="far fa-circle note-icon"></i>
                </div>
              </div>
              <p class="note-description">${note.text}</p>
            </div>
          </div>
        </div>
      `
    }).join('')

    this.gameNotesListTarget.innerHTML = notesHtml
  }

  renderRecapTitle(sportData) {
    debugger;
    if (!this.hasRecapTitleTarget) return

    const recapTitle = sportData.recap_title
    this.recapTitleTarget.textContent = recapTitle
  }
}
